/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement} from 'react';
import {Button, ButtonProps} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '../../../types/types';
import {StableButtonLabel} from './StableButtonLabel';

export interface SecondaryButtonProps extends ButtonProps {
  children: string;
  icon?: ReactElement;
}

const iconStyles = {
  '& .MuiButton-startIcon' : {
    color       : 'inherit',
    marginLeft  : '0 !important',
    marginRight : '0 !important',
    '& svg'     : {
      color : 'inherit'
    },
    '& svg [fill]:not([fill="none"])' : {
      fill : 'currentColor'
    },
    '& svg [stroke]:not([stroke="none"])' : {
      stroke : 'currentColor'
    }
  }
};

const getCommonButtonStyles = (theme: DLSTheme): any => ({
  height          : '32px',
  minHeight       : '32px',
  borderWidth     : '1px',
  borderStyle     : 'solid',
  borderColor     : dlsColor('pastelTeal'),
  padding         : '0px 8px',
  borderRadius    : '8px !important',
  backgroundColor : dlsColor('white'),
  color           : dlsColor('darkTeal'),
  fontSize        : '14px',
  fontFamily      : 'Inter',
  fontWeight      : 500,
  lineHeight      : '20px',
  boxSizing       : 'border-box',
  ...iconStyles,
  '&:hover'       : {
    backgroundColor : dlsColor('aquamarine'),
    borderColor     : dlsColor('pastelTeal'),
    borderWidth     : '1px',
    boxShadow       : 'none',
    color           : dlsColor('darkTeal'),
    padding         : '0px 8px'
  },
  '&:active' : {
    backgroundColor : dlsColor('aquamarine'),
    borderColor     : dlsColor('lightTeal'),
    borderWidth     : '1px',
    color           : dlsColor('darkTeal')
  },
  '&[aria-pressed="true"]' : {
    backgroundColor : dlsColor('aquamarine'),
    borderColor     : dlsColor('lightTeal'),
    borderWidth     : '1px',
    color           : dlsColor('darkTeal')
  },
  '&:focus' : {
    outline : 'none'
  },
  '&:focus-visible' : {
    gap           : '8px',
    filter        : 'none',
    display       : 'flex',
    outline       : `1px solid ${dlsColor('skyBlue')}`,
    padding       : '4px 8px',
    borderRadius  : '8px',
    outlineOffset : '1px'
  },
  '&:disabled' : {
    backgroundColor : dlsColor('grey8'),
    borderColor     : dlsColor('grey7'),
    color           : dlsColor('grey4')
  }
});

const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    padding : '3px',
    width   : '100%'
  },
  disabledContainer : {
    outline : 'none'
  },
  root : {
    ...getCommonButtonStyles(theme)
  },
  rootAlt : {
    width : '100%',
    ...getCommonButtonStyles(theme)
  },
  disabled : {}
}));

/**
 * Secondary button component used for medium-emphasis actions.
 *
 * ```typescript
 * import React from 'react';
 * import {SecondaryButton} from '@ion/dls/src/components/buttons';
 *
 * const Example = () => (
 *   <>
 *     <SecondaryButton>Default</SecondaryButton>
 *     <SecondaryButton disabled>Disabled</SecondaryButton>
 *     <SecondaryButton fullWidth>Full Width</SecondaryButton>
 *   </>
 * );
 * ```
 */
const SecondaryButton = ({
  children,
  icon,
  startIcon,
  ...props
}: SecondaryButtonProps) => {
  const classes = useStyles();
  return (
    <div className={props.disabled ? classes.disabledContainer : classes.container}>
      <Button variant='outlined'
        tabIndex={props.disabled ? -1 : 0}
        classes={{
          root     : props.fullWidth ? classes.rootAlt : classes.root,
          disabled : classes.disabled
        }}
        disableRipple
        disableFocusRipple
        startIcon={icon || startIcon}
        {...props}>
        <StableButtonLabel>{children}</StableButtonLabel>
      </Button>
    </div>
  );
};

export {SecondaryButton};
